import { NAV_SECTIONS, NavSection, NavNestedSection, NavItem } from '../index';

/**
 * Checks if a child is a NavItem
 */
export const isNavItem = (child: NavItem | NavNestedSection): child is NavItem => {
  return 'label' in child;
};

/**
 * Checks if a child is a NavNestedSection
 */
export const isNavNestedSection = (child: NavItem | NavNestedSection): child is NavNestedSection => {
  return 'title' in child;
};

/**
 * Checks if a pathname matches a route (handles CircleCI artifact URLs)
 */
const matchesRoute = (pathname: string, route: string): boolean => {
  // For artifact links, pathname is anything after '/0'
  return (pathname.split('/0')[1] || pathname) === route;
};

/**
 * Checks if a route(s) is currently active
 */
export const isRouteActive = (pathname: string, routes?: string[]): boolean => {
  if (!routes) {
    return false;
  }
  return routes.some(route => matchesRoute(pathname, route));
};

/**
 * Recursively checks if a section has any active children (i.e. items or nested sections)
 */
export const hasActiveChild = (pathname: string, section: NavSection | NavNestedSection): boolean => {
  // NavNestedSection
  if ('items' in section) {
    return section.items.some(item => isRouteActive(pathname, item.route));
  }

  // NavSection
  if ('children' in section) {
    return section.children.some(child => {
      return isNavItem(child) ? isRouteActive(pathname, child.route) : hasActiveChild(pathname, child);
    });
  }

  return false;
};

/**
 * Checks if a section should be expanded (itself is active or it contains an active child)
 */
export const shouldSectionBeExpanded = (pathname: string, section: NavSection | NavNestedSection): boolean => {
  return isRouteActive(pathname, section.route) || hasActiveChild(pathname, section);
};

export const navBubble = (bubble?: string | number) =>
  bubble != null ? { type: 'orange' as const, text: bubble } : undefined;

export type TopLevelNavItem = {
  label: string;
  route: string;
  id: string;
  isExternal?: boolean;
};

/**
 * Extract top-level navigation items for mobile dropdown
 * Returns items with label (NavItem) and section titles (NavSection)
 * Excludes dividers and nested children
 */
export const getTopLevelNavItems = (): TopLevelNavItem[] => {
  const items: TopLevelNavItem[] = [];

  NAV_SECTIONS.forEach(item => {
    // Skip dividers
    if ('type' in item) return;

    // NavItem (has label property)
    if ('label' in item) {
      const navItem = item as NavItem;
      // Skip preferences as it opens a modal
      if (navItem.id === 'preferences') return;

      items.push({
        label: navItem.label,
        route: navItem.route[0],
        id: navItem.id,
        isExternal: navItem.target === '_blank',
      });
    }

    // NavSection (has title property)
    if ('title' in item && 'children' in item) {
      const navSection = item as NavSection;
      const route = navSection.route?.[0];
      if (!route) return;
      items.push({
        label: navSection.title,
        route,
        id: navSection.id || navSection.title.toLowerCase(),
      });
    }
  });

  return items;
};
